import type { TcEvent } from "@theconversation/shared-components/tcEventsCollector"
import {
  deviceType,
  findOrCreateDeviceId,
  pageIsDraft,
  userIsWebCrawler,
} from "@theconversation/shared-components/tcEventsCollector"
import { isDevelopment } from "common/lib/environments"
import { RegionConfiguration } from "modules/i18n/utils/loadRegionConfiguration"
import getConfig from "next/config"

// This module implements a very simple event dispatch system.
// It will try to use a persistent deviceId for a given user's browser.
// The general schema of the events is common data at the top level, and event-specific
// data nested under a key named after the event.

// In the current iteration, article view and promo events are blocked if they are
// coming from a draft editor preview. Potentially a future version should instead
// set an attribute (like "location: draft") instead, but while this is an experiment
// it's better to keep things simple.

// In order to be able to mock this out in tests, we need to only fetch the value once methods are called.
// It's also better practice to fetch this on demand rather than at page load. So, this loads on demand
// and memoizes it.
var userEventCollectorUrlValue

function userEventCollectorUrl(): string {
  userEventCollectorUrlValue ||= getConfig().publicRuntimeConfig.USER_EVENT_COLLECTOR_URL
  return userEventCollectorUrlValue
}

export function trackPromoViewed(promoId: string, promoType: string, contentId = "") {
  if (pageIsDraft()) return

  const event = {
    eventType: "promoViewed",
    deviceId: findOrCreateDeviceId(),
    promoViewed: {
      contentId: contentId.toString(),
      promoId: promoId.toString(),
      promoType: promoType,
      deviceType: deviceType(),
    },
  }
  dispatchEvent(event)
}

export function trackPromoEngaged(promoId: string, promoType: string, contentId = "") {
  if (pageIsDraft()) return

  const event = {
    eventType: "promoEngaged",
    deviceId: findOrCreateDeviceId(),
    promoEngaged: {
      contentId: contentId.toString(),
      promoId: promoId.toString(),
      promoType: promoType,
      deviceType: deviceType(),
    },
  }
  dispatchEvent(event)
}

export function trackCallToActionViewed(callToActionId: string) {
  if (pageIsDraft()) return

  const event = {
    eventType: "callToActionViewed",
    deviceId: findOrCreateDeviceId(),
    callToActionViewed: {
      callToActionId: callToActionId,
      deviceType: deviceType(),
    },
  }
  dispatchEvent(event)
}

export function trackCallToActionEngaged(callToActionId: string) {
  if (pageIsDraft()) return

  const event = {
    eventType: "callToActionEngaged",
    deviceId: findOrCreateDeviceId(),
    callToActionEngaged: {
      callToActionId: callToActionId,
      deviceType: deviceType(),
    },
  }
  dispatchEvent(event)
}

// Checks if we're currently trying to track users in this region.
// Ultimately this will need to integrate with Didomi, but for now it
// just returns true if Didomi is _not_ enabled for the region.
export function regionIsEligibleToTrack(regionConfig: RegionConfiguration): boolean {
  const isDidomiRegionEnabled = Boolean(regionConfig?.didomi?.enabled)
  return !isDidomiRegionEnabled
}

// Dispatch the event to our event collector.
// This function tries to avoid any exceptions bubbling up.
// If the analytics event can't be sent, just log and then let it be discarded.
function dispatchEvent(event: TcEvent) {
  if (userIsWebCrawler()) return

  if (userEventCollectorUrl() == undefined) {
    if (isDevelopment()) {
      console.log("User event collector URL not defined")
    }
    return
  }

  try {
    const eventUrl = userEventCollectorUrl() + "/v1/event"
    if (isDevelopment()) {
      console.log("Dispatching user analytics event, type: " + event.eventType)
    }

    fetch(eventUrl, {
      method: "POST",
      keepalive: true, // Requests that the request is still sent even if user navigates away. (eg. promo engagement)
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(event),
    })
      .catch((err) => {
        console.warn("Failed to dispatch event: " + err)
      })
      .then((response) => {
        if (response && response.status != 202) {
          console.warn(`Unusual status from event collector: ${response.status}`)
        }
      })
  } catch (err) {
    console.warn("Failed to dispatch event: " + err)
  }
}
